/**
 * EventMarkers — list of notable events (cards played, stones seized,
 * victory declared) rendered as a clickable timeline. Clicking a marker
 * jumps the playback cursor to that event.
 */

import type { ReactElement } from 'react';
import type { ReplayEvent, ReplayPlaybackHook } from './useReplayPlayback.js';

type MarkerKind = 'card_play' | 'stone_seized' | 'victory' | 'concede' | 'emote';

type Marker = {
  index: number;
  kind: MarkerKind;
  label: string;
  actor: string | null;
};

function classifyEvent(ev: ReplayEvent, idx: number): Marker | null {
  if (ev.kind !== 'TableActionApplied') return null;
  const action = ev.payload?.action as
    | { kind?: string; cardId?: string; from?: { kind?: string }; to?: { kind?: string } }
    | undefined;
  const actor = (ev.payload?.actor as string | undefined) ?? null;
  if (!action) return null;

  if (action.kind === 'MoveCard' && action.from?.kind === 'hand' && action.to?.kind === 'realm') {
    return { index: idx, kind: 'card_play', label: `play ${action.cardId ?? '?'}`, actor };
  }
  if (action.kind === 'MoveStone') {
    return { index: idx, kind: 'stone_seized', label: 'stone moved', actor };
  }
  if (action.kind === 'DeclareVictory') {
    return { index: idx, kind: 'victory', label: 'victory declared', actor };
  }
  if (action.kind === 'ConcedeMatch') {
    return { index: idx, kind: 'concede', label: 'concede', actor };
  }
  if (action.kind === 'EmoteReaction') {
    return { index: idx, kind: 'emote', label: '😀 emote', actor };
  }
  return null;
}

const KIND_COLOR: Record<MarkerKind, string> = {
  card_play: 'bg-amber-500',
  stone_seized: 'bg-violet-500',
  victory: 'bg-emerald-500',
  concede: 'bg-rose-500',
  emote: 'bg-sky-500',
};

type Props = {
  hook: ReplayPlaybackHook;
};

export function EventMarkers({ hook }: Props): ReactElement {
  const markers: Marker[] = [];
  for (let i = 0; i < hook.events.length; i++) {
    const ev = hook.events[i];
    if (!ev) continue;
    const m = classifyEvent(ev, i);
    if (m) markers.push(m);
  }

  if (markers.length === 0) {
    return <div className="text-xs text-cosmos-500 italic">No notable events.</div>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {markers.map((m) => {
        const passed = m.index <= hook.cursor;
        return (
          <button
            type="button"
            key={`${m.index}-${m.kind}`}
            onClick={() => hook.seekTo(m.index)}
            className={`flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-white ${
              passed ? KIND_COLOR[m.kind] : 'bg-cosmos-700 text-cosmos-300'
            } hover:opacity-90`}
            title={`${m.actor ?? 'unknown'} — ${m.label}`}
          >
            <span className="font-mono">{m.index + 1}</span>
            <span>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}
